import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import PayrollReportFilters from "../../components/PayrollReports/PayrollReportFilters";
import PayrollBatchTable from "../../components/PayrollReports/PayrollBatchTable";
import { ScheduleJobList4, ListTransFileRecs, GetMerchantProfile } from "../../util/API/batch";
import { useSelector } from "react-redux";
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth } from "date-fns";
import { decodeHtmlEntities } from "../../util/utilfunctions/utilFunctions";

const PAYROLL_CATEGORY_ID = "33";

// ─── Defined at module level — available to both fetchAllBatches and applyFilters ───
function parseCustomDate(dateStr) {
  if (!dateStr || dateStr === "—") return new Date(0); // push invalid dates to bottom
  const parts = dateStr.trim().split(" ");
  const datePart = parts[0];
  const timePart = parts[1] || "00:00:00";
  const [year, month, day] = datePart.split("-").map(Number);
  const [hour, minute, second] = timePart.split(":").map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return new Date(0);
  return new Date(year, month - 1, day, hour || 0, minute || 0, second || 0);
}

function PayrollAdminReports() {
  const [batches, setBatches] = useState([]);
  const [filteredBatches, setFilteredBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [duration, setDuration] = useState("1");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [companyName, setCompanyName] = useState("Unknown_Company");
  const merchantInfo = useSelector((state) => state.login.user);

  // ─── Fetch company name from merchant profile ───
  useEffect(() => {
    const fetchCompanyName = async () => {
      if (merchantInfo?.id) {
        try {
          const res = await GetMerchantProfile("", merchantInfo.id);
          if (res?.callStatus === "1") {
            const businessname = res.payload?.Merchant?.businessname || "Unknown_Company";
            setCompanyName(
              businessname
                .replace(/&amp;amp;/g, "&")
                .replace(/&amp;#039;/g, "'")
                .replace(/&amp;#39;/g, "'")
                .replace(/&amp;/g, "&")
                .replace(/&quot;/g, '"')
                .replace(/&#34;/g, '"')
                .replace(/&#039;/g, "'")
                .replace(/&lt;/g, "<")
                .replace(/&gt;/g, ">")
                .replace(/&#39;/g, "'")
                .trim()
            );
          }
        } catch (err) {
          console.error("Failed to fetch merchant profile:", err);
        }
      }
    };
    fetchCompanyName();
  }, [merchantInfo?.id]);

  // ─── Fetch all batches for this user ───
  const fetchAllBatches = async () => {
    setLoading(true);
    try {
      const response = await ScheduleJobList4({
        categoryid: PAYROLL_CATEGORY_ID,
        rows: 1000,
      });

      if (response.callStatus === "1") {
        let raw = response.payload?.record || [];
        if (!Array.isArray(raw)) raw = raw ? [raw] : [];

        const mapped = raw
          .map((item) => {
            const s = item.Scheduleinstance;
            const recordsMatch = s.notes?.match(/records:\s*(\d+)/i);
            const totalRecords = recordsMatch ? parseInt(recordsMatch[1], 10) : 0;
            const scheduledDateStr = s.scheduletime || s.requesttime || "—";

            return {
              id: s.requestid,
              fileName: s.inputfilename || "",
              records: totalRecords,
              scheduledDateStr,
              statusCode: s.executionstatus,
              status: getStatusDisplay(s.executionstatus),
              notes: s.notes || "",
              requestuserid: s.requestuserid,
              totalAmount: parseFloat(s.amount) || 0,
              totalAmountDisplay: parseFloat(s.amount)
                ? parseFloat(s.amount).toLocaleString("en-US", { minimumFractionDigits: 2 })
                : "—",
            };
          })
          // ─── Filter by logged-in user's ID — immune to special characters ───
          .filter((batch) => batch.requestuserid === String(merchantInfo?.id))
          // ─── Sort descending by scheduled date — latest first ───
          .sort((a, b) => parseCustomDate(b.scheduledDateStr) - parseCustomDate(a.scheduledDateStr));

        // ─── Apply DAILY filter on initial load ───
        const today = format(new Date(), "yyyy-MM-dd");
        const dailyFiltered = mapped.filter(
          (b) => b.scheduledDateStr?.split(" ")[0] === today
        );

        setBatches(mapped);
        setFilteredBatches(dailyFiltered);
      } else {
        setBatches([]);
        setFilteredBatches([]);
      }
    } catch (err) {
      console.error("Failed to fetch payroll batches:", err);
      setBatches([]);
      setFilteredBatches([]);
    } finally {
      setLoading(false);
    }
  };

  // ─── Trigger fetch only once company name is resolved ───
  useEffect(() => {
    if (companyName && companyName !== "Unknown_Company") {
      fetchAllBatches();
    }
  }, [companyName]);

  // ─── Apply filters when Run Report is clicked ───
  const applyFilters = () => {
    let filtered = [...batches];
    const now = new Date();

    if (duration === "1") {
      // ─── Daily: compare date string directly — no timezone risk ───
      const today = format(now, "yyyy-MM-dd");
      filtered = filtered.filter(
        (b) => b.scheduledDateStr && b.scheduledDateStr.split(" ")[0] === today
      );

    } else if (duration === "2") {
      // ─── Weekly: Monday to Sunday, local time ───
      const weekStart = startOfWeek(now, { weekStartsOn: 1 });
      const weekEnd = endOfWeek(now, { weekStartsOn: 1 });
      weekStart.setHours(0, 0, 0, 0);
      weekEnd.setHours(23, 59, 59, 999);
      filtered = filtered.filter((b) => {
        const d = parseCustomDate(b.scheduledDateStr);
        return d && d >= weekStart && d <= weekEnd;
      });

    } else if (duration === "3") {
      // ─── Monthly: current month, local time ───
      const monthStart = startOfMonth(now);
      const monthEnd = endOfMonth(now);
      monthStart.setHours(0, 0, 0, 0);
      monthEnd.setHours(23, 59, 59, 999);
      filtered = filtered.filter((b) => {
        const d = parseCustomDate(b.scheduledDateStr);
        return d && d >= monthStart && d <= monthEnd;
      });

    } else if (duration === "4" && startDate && endDate) {
      // ─── Custom range: parse input dates as local time ───
      const [sy, sm, sd] = startDate.split("-").map(Number);
      const [ey, em, ed] = endDate.split("-").map(Number);
      const start = new Date(sy, sm - 1, sd, 0, 0, 0, 0);
      const end = new Date(ey, em - 1, ed, 23, 59, 59, 999);

      filtered = filtered.filter((b) => {
        const d = parseCustomDate(b.scheduledDateStr);
        return d && d >= start && d <= end;
      });
    }

    setFilteredBatches(filtered);
  };

  const handleRunReport = () => {
    applyFilters();
  };

  // ─── Download CSV ───
  const handleDownloadCSV = async (batch) => {
    try {
      const res = await ListTransFileRecs(batch.id);

      if (res.callStatus !== "1") {
        alert("Could not load records for download.");
        return;
      }

      const rawArray = res.payload?.record || [];
      const recordsArray = Array.isArray(rawArray) ? rawArray : rawArray ? [rawArray] : [];
      const allRecords = recordsArray.map((item) => item.Transfilerecs).filter(Boolean);

      if (allRecords.length === 0) {
        alert("No records to download.");
        return;
      }

      const headers = [
        "First Name", "Last Name", "Email", "Phone", "Amount", "Currency",
        "Address Line 1", "Address Line 2", "Country", "State", "Zip Code",
        "Date of Birth", "Comment", "Purpose of Account", "Status", "Message",
      ];

      const rows = allRecords.map((r) => [
        r.firstname || "",
        r.lastname || "",
        r.email || "",
        r.phoneno || "",
        r.amount || "",
        r.currency || "",
        r.address1 || "",
        r.address2 || "",
        r.country || "",
        r.state || "",
        r.zipcode || "",
        r.birthdate || "",
        r.comment || "",
        r.purposeofaccount || "",
        r.status,
        r.status === "3" ? `amount ${r.amount} successfully deposited.` : "",
      ]);

      const csv = [
        headers.join(","),
        ...rows.map((row) =>
          row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
        ),
      ].join("\n");

      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${batch.fileName.replace(".csv", "")}_records.csv`;
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 40000);
    } catch (err) {
      console.error("Download failed:", err);
      alert("Error generating CSV file.");
    }
  };

  // ─── Totals from filtered batches ───
  const totalEmployees = filteredBatches.reduce((sum, b) => sum + b.records, 0);
  const grandTotalAmount = filteredBatches.reduce(
    (sum, b) => sum + (parseFloat(b.totalAmount) || 0),
    0
  );

  const getStatusDisplay = (code) => {
    const map = {
      "-2": "Open",
      "-1": "Review",
      "0": "Approved for Processing",
      "1": "Approved for Processing",
      "5": "Rejected",
      "3": "Completed",
      "2": "Error",
      "4": "Cancelled",
    };
    return map[code] || `Unknown (${code})`;
  };

  return (
    <Box className="page-container">
      <Box className="header-container" sx={{ mb: 4 }}>
        <Typography className="page-title">Payroll Reports</Typography>
      </Box>

      <Box sx={{ mb: 4, fontStyle: "italic", color: "#555" }}>
        Company: <strong>{decodeHtmlEntities(companyName)}</strong>
      </Box>

      <PayrollReportFilters
        duration={duration}
        setDuration={setDuration}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        onRunReport={handleRunReport}
      />

      {/* Summary Box */}
      <Box
        sx={{
          backgroundColor: "#5523D2",
          color: "white",
          p: 3,
          borderRadius: 2,
          mb: 4,
          textAlign: "center",
          fontSize: "1.3rem",
          fontWeight: "bold",
        }}
      >
        Total Employees: {totalEmployees} &nbsp;&nbsp;|&nbsp;&nbsp;
        Total Amount:{" "}
        <strong>
          ${grandTotalAmount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
        </strong>
      </Box>

      <PayrollBatchTable
        batches={filteredBatches}
        loading={loading && batches.length === 0}
        companyName={companyName}
        onDownload={handleDownloadCSV}
      />
    </Box>
  );
}

export default PayrollAdminReports;
